import { uploadsApi } from '@gl/api-client';
import { UPLOAD_CONTENT_TYPES, uploadFileError } from '@gl/shared';
import { ImageOff, ImagePlus, Link2, RefreshCw, Trash2, Upload } from 'lucide-react';
import { useEffect, useId, useRef, useState, type DragEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ApiError, api, errorMessage } from '@/lib/api';
import { cn } from '@/lib/utils';

const uploads = uploadsApi(api);

/**
 * Si el servidor no tiene R2 (503 `UPLOADS_NOT_CONFIGURED`) o no deja subir (demo), lo recordamos
 * para toda la sesión: los demás `ImageUpload` muestran directo el campo URL.
 */
let uploadsUnavailable: string | null = null;
const UNAVAILABLE_CODES = new Set(['UPLOADS_NOT_CONFIGURED', 'DEMO_RESTRICTED']);

/** PUT con progreso (fetch no informa progreso de subida). */
function putWithProgress(
  url: string,
  file: File,
  headers: Record<string, string>,
  onProgress: (pct: number) => void,
  signal: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(Math.round((e.loaded / e.total) * 100));
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`No pudimos subir la imagen (${xhr.status})`));
    xhr.onerror = () => reject(new Error('No pudimos subir la imagen: revisá tu conexión'));
    xhr.onabort = () => reject(new DOMException('Cancelado', 'AbortError'));
    signal.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(file);
  });
}

export interface ImageUploadProps {
  localId: string;
  /** URL actual ('' = sin imagen). */
  value: string;
  onChange: (url: string) => void;
  /** Error de validación del form (p. ej. URL inválida): solo marca el input; el texto lo muestra FormField. */
  error?: string;
  /** Avisa mientras hay una subida en curso (para deshabilitar "Guardar"). */
  onBusyChange?: (busy: boolean) => void;
  disabled?: boolean;
  /** `square` para logos, `wide` para fotos de productos y categorías. */
  shape?: 'square' | 'wide';
  /** id del input de URL (para el `htmlFor` del label). */
  id?: string;
}

/**
 * Imagen con subida directa a R2 (arrastrar o elegir archivo, preview, progreso, quitar) y, como
 * alternativa, el campo para pegar un link. Si el servidor no tiene uploads, queda solo el link.
 */
export function ImageUpload({
  localId,
  value,
  onChange,
  error,
  onBusyChange,
  disabled,
  shape = 'wide',
  id,
}: ImageUploadProps) {
  const autoId = useId();
  const urlId = id ?? `${autoId}-url`;
  const fileRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [unavailable, setUnavailable] = useState<string | null>(uploadsUnavailable);
  const [showUrl, setShowUrl] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [broken, setBroken] = useState(false);

  const busy = progress !== null;
  const urlMode = showUrl || !!unavailable;

  useEffect(() => () => abortRef.current?.abort(), []);
  useEffect(() => onBusyChange?.(busy), [busy, onBusyChange]);
  useEffect(() => () => {
    if (localPreview) URL.revokeObjectURL(localPreview);
  }, [localPreview]);
  useEffect(() => setBroken(false), [value]);

  async function upload(file: File) {
    const invalid = uploadFileError(file);
    if (invalid) {
      setUploadError(invalid);
      return;
    }
    setUploadError(null);
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLocalPreview(URL.createObjectURL(file));
    setProgress(0);
    try {
      const presign = await uploads.presign(localId, { contentType: file.type as (typeof UPLOAD_CONTENT_TYPES)[number], size: file.size });
      await putWithProgress(presign.uploadUrl, file, presign.headers, setProgress, ctrl.signal);
      onChange(presign.publicUrl);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      if (err instanceof ApiError && UNAVAILABLE_CODES.has(err.code)) {
        uploadsUnavailable = err.message;
        setUnavailable(err.message);
      } else {
        setUploadError(errorMessage(err, 'No pudimos subir la imagen'));
      }
    } finally {
      if (abortRef.current === ctrl) {
        abortRef.current = null;
        setProgress(null);
        setLocalPreview(null);
      }
    }
  }

  function onFiles(files: FileList | null) {
    const file = files?.[0];
    if (file) void upload(file);
    if (fileRef.current) fileRef.current.value = '';
  }

  function onDrop(e: DragEvent) {
    e.preventDefault();
    setDragging(false);
    if (!disabled && !busy && !unavailable) onFiles(e.dataTransfer.files);
  }

  const shown = localPreview ?? (value || null);
  const frame = shape === 'square' ? 'size-24' : 'h-28 w-40';

  return (
    <div className="grid gap-2">
      {!unavailable && (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled && !busy) setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={cn(
            'flex items-center gap-3 rounded-lg border border-dashed p-3 transition',
            dragging && 'border-primary bg-primary/5',
            disabled && 'opacity-60',
          )}
        >
          <div className={cn('relative shrink-0 overflow-hidden rounded-md border bg-muted', frame)}>
            {shown && !broken ? (
              <img
                src={shown}
                alt=""
                className="size-full object-cover"
                onError={() => !localPreview && setBroken(true)}
              />
            ) : (
              <span className="flex size-full items-center justify-center text-muted-foreground">
                {broken ? <ImageOff className="size-6" /> : <ImagePlus className="size-6" />}
              </span>
            )}
            {busy && (
              <span className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-background/70 text-xs font-medium">
                {progress}%
                <span className="h-1 w-3/4 overflow-hidden rounded-full bg-muted">
                  <span className="block h-full bg-primary transition-[width]" style={{ width: `${progress}%` }} />
                </span>
              </span>
            )}
          </div>
          <div className="grid min-w-0 flex-1 gap-1.5">
            <p className="text-xs text-muted-foreground">
              {busy
                ? 'Subiendo…'
                : broken
                  ? 'No pudimos mostrar esta imagen. Revisá el link o subí otra.'
                  : 'Arrastrá una imagen o elegí un archivo. JPG, PNG o WebP, hasta 5 MB.'}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled || busy}
                onClick={() => fileRef.current?.click()}
              >
                {value ? <RefreshCw /> : <Upload />} {value ? 'Cambiar' : 'Subir imagen'}
              </Button>
              {busy ? (
                <Button type="button" variant="ghost" size="sm" onClick={() => abortRef.current?.abort()}>
                  Cancelar
                </Button>
              ) : (
                value && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-destructive"
                    disabled={disabled}
                    onClick={() => onChange('')}
                  >
                    <Trash2 /> Quitar
                  </Button>
                )
              )}
            </div>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept={UPLOAD_CONTENT_TYPES.join(',')}
            className="hidden"
            disabled={disabled || busy}
            onChange={(e) => onFiles(e.target.files)}
          />
        </div>
      )}

      {urlMode ? (
        <div className="grid gap-1.5">
          {unavailable && <p className="text-xs text-muted-foreground">{unavailable}</p>}
          <div className="flex items-center gap-2">
            {unavailable && value && !broken && (
              <img src={value} alt="" className="size-10 shrink-0 rounded-md border object-cover" onError={() => setBroken(true)} />
            )}
            <Input
              id={urlId}
              type="url"
              inputMode="url"
              placeholder="https://…"
              value={value}
              disabled={disabled}
              aria-invalid={!!error}
              onChange={(e) => onChange(e.target.value)}
            />
          </div>
        </div>
      ) : (
        <button
          type="button"
          id={urlId}
          className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground hover:underline"
          disabled={disabled}
          onClick={() => setShowUrl(true)}
        >
          <Link2 className="size-3" /> Prefiero pegar un link
        </button>
      )}

      {uploadError && <p className="text-xs text-destructive">{uploadError}</p>}
    </div>
  );
}
